// components/PlanModule.jsx
import React, { useEffect, useMemo, useState } from "react";
import AWS from "aws-sdk";
import PlanManager from "../api/PlanManager";

import "./PlanModule.css";

/**
 * PlanModule
 *
 * Props:
 * - selectedRoom: string like "room:project_<uuid>:<room_slug>"
 * - onStartChat?: () => void  (optional CTA when no plan)
 * - maxTasks?: number         (default 4)
 *
 * Styles: reuses your existing CSS classes used in the Plan block
 *   - room-project-top-row, room-card-plan, room-card-box, plan-title, plan-list
 */
export default function PlanModule({
  selectedRoom,
  onStartChat,
  maxTasks = 4,
}) {
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);

  const { projectIdToken, roomIdSlug } = useMemo(
    () => parseRoomGlobalId(selectedRoom),
    [selectedRoom],
  );

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      setPlan(null);

      try {
        if (!selectedRoom || !projectIdToken || !roomIdSlug) {
          setLoading(false);
          return;
        }

        // Ensure AWS creds are resolved to get Cognito identity id
        if (!AWS?.config?.credentials) {
          throw new Error("AWS.config.credentials not set");
        }
        await AWS.config.credentials.getPromise();
        const cognitoId = AWS.config.credentials.identityId;
        if (!cognitoId) throw new Error("Missing Cognito identityId");

        const p = await PlanManager.loadPlan({
          cognitoId,
          projectId: projectIdToken,
          roomId: roomIdSlug,
        });

        if (!cancelled) setPlan(p && Object.keys(p).length ? p : null);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [selectedRoom, projectIdToken, roomIdSlug]);

  const tasks = useMemo(
    () => (Array.isArray(plan?.plan_json?.tasks) ? plan.plan_json.tasks : []),
    [plan],
  );

  const taskTitles = useMemo(
    () => tasks.map((t) => t?.title || "").filter(Boolean),
    [tasks],
  );

  return (
    <div className="room-project-top-row">
      <div className="room-card-plan" aria-label="RoomPlan">
        <div className="room-card-box">
          <h2 className="plan-title">Plan</h2>

          {loading && <div className="text-sm opacity-70">Loading plan…</div>}

          {error && (
            <div className="text-sm text-red-600">
              Couldn’t load plan: {String(error?.message || error)}
            </div>
          )}

          {!loading && !error && !selectedRoom && (
            <div className="text-sm opacity-80">
              Select a room to see its plan.
            </div>
          )}

          {!loading && !error && selectedRoom && !plan && (
            <div className="text-sm opacity-80">
              No plan yet. Start a quick chat with Harmony to create one for
              this room.
              {onStartChat && (
                <div className="mt-2">
                  <button
                    onClick={onStartChat}
                    className="px-3 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white text-sm"
                    type="button"
                  >
                    Chat with Harmony
                  </button>
                </div>
              )}
            </div>
          )}

          {!loading && !error && plan && (
            <>
              <ul className="plan-list">
                {taskTitles.slice(0, maxTasks).map((t, i) => (
                  <li key={i}>{t}</li>
                ))}
                {taskTitles.length === 0 && (
                  <li className="opacity-70">No tasks in this plan yet.</li>
                )}
              </ul>

              {plan?.plan_json?.next_action?.summary && (
                <div className="mt-2 text-xs opacity-80">
                  <strong>This week’s next step:</strong>{" "}
                  {plan.plan_json.next_action.summary}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function parseRoomGlobalId(globalId) {
  const m = /^room:([^:]+):(.+)$/.exec(String(globalId || ""));
  return {
    projectIdToken: m?.[1] || null,
    roomIdSlug: m?.[2] || null,
  };
}
